import React, { ChangeEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ContractPlatformTable from './ContractPlatformTable';
import { useDispatch, useSelector } from 'react-redux';
import { IAllStates } from '../../../../../../../../store/rootReducer';
import { OptionalItemsTable } from './OptionalItemsTable';
import { AdditionalItemsTable } from './AdditionalItemsTable';
import { Alert, Form } from 'react-bootstrap';
import _debounce from 'lodash/debounce';
import {
    setAnnexDescription,
    setPersonalOfferDescription,
    setInviteAttachment,
} from '../../../../../../../../store/brand/contractBrandSlice';
import { ContractHeader } from './ContractHeader';
import { ContractAnnexInfo } from '../../../../../../../../shared/components/offer/ContractAnnexInfo';
import { ContractService } from '../../../../service';
import { ErrorToast } from '../../../../../../../../utils/toasters';
import { ContractProposalReviewInfo } from './ContractProposalReviewInfo';
import { DeclineOffer } from '../../../../../../../influencer/offer/modal/offer-modal/tabs/offer/DeclineOffer';
import { Totals } from './Totals';
import { downloadFile, formatDate, getGrandTotal } from '../../../../../../../../shared/functions/Functions';
import { TotalTypes } from '../../../../../../../../enum/TotalTypes';
import { UploadImage } from '../../../../../../../../shared/components/UploadImage';
import FilePlaceholder from '../../../../../../../../shared/components/placeholder/FilePlaceholder';
import { Fancybox } from '../../../../../../../../shared/components/Fancybox';
import { Fancybox as NativeFancybox } from '@fancyapps/ui';
import { getTruncatedData } from '../../../../../../../../utils/helpers/getTruncatedData';
import TooltipOverlay from '../../../../../../../../shared/components/tooltip/Tooltip';

export const ContractTab = () => {
    const {
        campaign,
        initialContract,
        contractModalStatus,
        isTransparentPricing,
        isCharity,
        inviteAttachment,
        isAnnexMode,
    } = useSelector((state: IAllStates) => state.contractBrand);
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isNegotiate = contractModalStatus === 'negotiate';
    const feePercentage = Math.round((1 - Number(campaign?.feePercentage ?? 0.0)) * 100);
    const subTotalSum = getGrandTotal(initialContract.contractItems, initialContract.distribution);
    const totalLiveFeeSum = Math.ceil(subTotalSum / (campaign?.feePercentage ?? 1)) - subTotalSum;
    const transparentGrandTotal = isNegotiate
        ? Math.ceil(subTotalSum / (campaign?.feePercentage ?? 1))
        : campaign?.contracts[0]?.totalWithFee ?? 0;
    const legalDocument = campaign?.contracts[0]?.legalDocuments?.[0];
    const { truncatedName: truncatedFileName, isTruncated: isFileNameTruncated } = getTruncatedData(
        legalDocument?.originalName ?? '',
        30
    );
    const { truncatedName: truncatedUploaderName, isTruncated: isUploaderNameTruncated } = getTruncatedData(
        legalDocument?.uploadedBy?.displayName ?? '',
        20
    );

    const displayFiles =
        inviteAttachment !== undefined
            ? inviteAttachment
            : contractModalStatus === 'negotiate' && legalDocument
            ? [
                  {
                      name: legalDocument.originalName ?? legalDocument.name,
                      type: legalDocument.mime,
                  } as unknown as Blob,
              ]
            : undefined;

    useEffect(() => {
        if (contractModalStatus === 'negotiate' && legalDocument && inviteAttachment === undefined) {
            dispatch(
                setInviteAttachment([
                    {
                        name: legalDocument.originalName ?? legalDocument.name,
                        type: legalDocument.mime,
                    } as unknown as Blob,
                ])
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contractModalStatus, legalDocument?.id]);

    const changeHandler = _debounce((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = event?.target?.value;
        dispatch(setPersonalOfferDescription(value));
    }, 300);

    const saveInviteFile: React.Dispatch<React.SetStateAction<Blob[] | MediaSource[] | undefined>> = value => {
        const resolvedFiles = typeof value === 'function' ? value(inviteAttachment) : value;
        const normalizedFiles = Array.isArray(resolvedFiles)
            ? resolvedFiles
            : resolvedFiles
            ? [resolvedFiles]
            : undefined;
        dispatch(setInviteAttachment(normalizedFiles as Blob[] | MediaSource[] | undefined));
    };

    const savePurchaseOrder = async (value: string): Promise<boolean> => {
        try {
            await ContractService.updatePurchaseOrder(campaign?.contracts[0]?.id as number, value);
            return true;
        } catch (e) {
            ErrorToast(e);
            return false;
        }
    };

    return (
        <>
            <DeclineOffer
                declineReason={campaign?.contracts[0]?.declineReason}
                declineComment={campaign?.contracts[0]?.declineComment}
                status={campaign?.contracts[0]?.status}
                cancellationNote={campaign?.contracts[0]?.cancellationNote}
                cancellationDocumentName={campaign?.contracts[0]?.cancellationDocumentName}
                cancellationDocumentPath={campaign?.contracts[0]?.cancellationDocumentPath}
                cancellationDocumentUploadedAt={campaign?.contracts[0]?.cancellationDocumentUploadedAt}
            />
            {isAnnexMode && (
                <>
                    <Alert variant="info" className="mb-3">
                        <h6 className="mb-2">{t('brand.campaign.book.contract.modal.annex.editor_title')}</h6>
                        <ul className="mb-0 pl-3">
                            <li>{t('brand.campaign.book.contract.modal.annex.editor_change')}</li>
                            <li>{t('brand.campaign.book.contract.modal.annex.editor_minimum')}</li>
                            <li>{t('brand.campaign.book.contract.modal.annex.editor_sign')}</li>
                        </ul>
                    </Alert>
                    <Form.Group className="mb-3">
                        <Form.Label>
                            {t('brand.campaign.book.contract.modal.annex.annex_description')}
                            <span className="text-danger"> *</span>
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            required
                            value={initialContract.annexDescription ?? ''}
                            onChange={event => dispatch(setAnnexDescription(event.target.value))}
                            placeholder={t('brand.campaign.book.contract.modal.annex.annex_description_placeholder')}
                        />
                    </Form.Group>
                </>
            )}
            <ContractAnnexInfo contract={campaign?.contracts[0]} hidden={isAnnexMode} />
            <ContractHeader
                createdAt={campaign?.contracts[0]?.createdAt ?? ''}
                contractItemId={campaign?.contracts[0]?.id ?? 0}
                status={campaign?.contracts[0]?.status}
                acceptedDate={
                    // An amendment shows when the influencer signed it, not when the first contract was accepted
                    campaign?.contracts[0]?.isAnnex
                        ? campaign?.contracts[0]?.annexAcceptedAt ?? campaign?.contracts[0]?.acceptedDate
                        : campaign?.contracts[0]?.acceptedDate
                }
                purchaseOrderInfluencer={campaign?.contracts[0]?.purchaseOrderInfluencer}
                onSavePurchaseOrder={savePurchaseOrder}
            />
            <ContractProposalReviewInfo
                isApproved={campaign?.contracts[0]?.isApproved}
                isDeclined={campaign?.contracts[0]?.isDeclined}
                isConvertedToOffer={campaign?.contracts[0]?.isConvertedToOffer}
                proposalDeclineReason={campaign?.contracts[0]?.proposalDeclineReason}
                proposalReviewedBy={campaign?.contracts[0]?.proposalReviewedBy}
                updatedAt={campaign?.contracts[0]?.updatedAt}
            />
            <div className="py-2">
                <ContractPlatformTable />
                <AdditionalItemsTable />
                {contractModalStatus === 'negotiate' &&
                    campaign?.contractPlaceholder?.distribution &&
                    campaign?.contractPlaceholder?.distribution?.length > 0 && <OptionalItemsTable />}
                {isTransparentPricing && !isCharity && (
                    <div className="w-100 mt-2">
                        <Totals title={TotalTypes.SUBTOTAL} price={subTotalSum} feePercentage={100 - feePercentage} />
                    </div>
                )}
                {isTransparentPricing ? (
                    <div className="w-100 mt-2">
                        <Totals
                            title={TotalTypes.FEE}
                            price={
                                contractModalStatus === 'negotiate'
                                    ? totalLiveFeeSum
                                    : campaign?.contracts[0]?.feeAmount ?? 0
                            }
                            feePercentage={feePercentage}
                            displayPlus={true}
                        />
                    </div>
                ) : (
                    <div className="my-3"></div>
                )}
                {isTransparentPricing && !isCharity && (
                    <div className="w-100 mt-2">
                        <Totals title={TotalTypes.GRAND} price={transparentGrandTotal} feePercentage={100} />
                    </div>
                )}
                {contractModalStatus === 'negotiate' && (
                    <>
                        <h6 className="mt-3">{t('influencer.offer_management.modal.add_personal_offer')}</h6>
                        <div className="row">
                            <div className="col-md-6">
                                <Form.Control
                                    id="basic-textarea"
                                    onChange={changeHandler}
                                    defaultValue={initialContract.personalOffer}
                                    as="textarea"
                                    name="personalOffer"
                                    rows={5}
                                    disabled={campaign?.isCharity}
                                    placeholder={t('influencer.offer_management.modal.add_personal_offer_placeholder')}
                                />
                            </div>
                            <div className="col-md-6 mt-3 mt-md-0">
                                <div className="offer-dropzone">
                                    <UploadImage
                                        saveFile={saveInviteFile}
                                        isMultiple={false}
                                        initialFiles={displayFiles}
                                        isFullHeight={true}
                                        acceptedFormats="application/pdf"
                                        centerText={true}
                                        fileName={true}
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                )}
                {contractModalStatus !== 'negotiate' && campaign?.contracts[0]?.personalOffer && (
                    <div className="mt-4 px-2">
                        <div>
                            <h6>{t('influencer.offer_management.modal.add_personal_offer')}</h6>
                        </div>
                        <p className="text-muted mb-0">{campaign?.contracts[0]?.personalOffer}</p>
                    </div>
                )}
                {contractModalStatus !== 'negotiate' && campaign?.contracts[0]?.legalDocuments?.[0] && (
                    <div className="mt-3 px-1">
                        <div
                            className="d-flex align-items-start p-2 border rounded position-relative"
                            style={{ maxWidth: 300 }}
                        >
                            <Fancybox>
                                <FilePlaceholder
                                    file={campaign?.contracts[0]?.legalDocuments?.[0]}
                                    hideText={true}
                                    classIcon="font-22"
                                    styleContent={{ width: 40, height: 40, flexShrink: 0 }}
                                    nameFancybox="legal-document"
                                    handleDownload={() =>
                                        downloadFile(
                                            campaign?.contracts[0]?.legalDocuments?.[0]?.path ?? '',
                                            campaign?.contracts[0]?.legalDocuments?.[0]?.originalName
                                        )
                                    }
                                    right={-10}
                                />
                            </Fancybox>
                            <div className="ms-2">
                                <TooltipOverlay content={isFileNameTruncated ? legalDocument?.originalName : ''}>
                                    <p
                                        className="mb-0 fw-semibold cursor-pointer"
                                        onClick={() =>
                                            NativeFancybox.show([
                                                {
                                                    src: campaign?.contracts[0]?.legalDocuments?.[0]?.path,
                                                    type: 'iframe',
                                                },
                                            ])
                                        }
                                    >
                                        {truncatedFileName || '-'}
                                    </p>
                                </TooltipOverlay>
                                <p className="mb-0 text-muted fs-12">
                                    {t('influencer.offer_management.modal.uploaded_by')}:{' '}
                                    <TooltipOverlay
                                        content={isUploaderNameTruncated ? legalDocument?.uploadedBy?.displayName : ''}
                                        dFlex={false}
                                    >
                                        <span>{truncatedUploaderName || '-'}</span>
                                    </TooltipOverlay>
                                </p>
                                <p className="mb-0 text-muted fs-12">
                                    {t('influencer.offer_management.modal.uploaded_at')}:{' '}
                                    {formatDate(campaign?.contracts[0]?.legalDocuments?.[0]?.updatedAt)}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default ContractTab;
